import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { fmt2 } from '@/lib/utils';
import {
  getTransactions, getRatingCategories, submitRating, getGroupRatings,
  markTransactionShipped, markTransactionDelivered, getShippingCarriers, uploadShippingDocument,
  createMarketplacePaymentCheckout, downloadMarketplaceInvoice
} from '@/lib/api';
import {
  ArrowLeftRight, Star, CheckCircle, Clock, XCircle, Building2,
  Package, TrendingUp, MessageSquare, Filter, Truck, PackageCheck, Upload, ExternalLink, Loader2,
  CreditCard, CircleDollarSign, AlertCircle, Hash, FileText, AlertTriangle, Ban
} from 'lucide-react';
import { OrderTimelineDialog } from '@/components/transactions/OrderTimelineDialog';
import { CancelDialog } from '@/components/transactions/CancelDialog';
import { DisputeDialog } from '@/components/transactions/DisputeDialog';
import { DisputeThreadDialog } from '@/components/transactions/DisputeThreadDialog';

const PAYMENT_CONFIG = {
  unpaid: {
    label: 'Zahlung ausstehend',
    color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    icon: AlertCircle,
  },
  pending: {
    label: 'Zahlung läuft',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    icon: Clock,
  },
  paid: {
    label: 'Bezahlt',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    icon: CircleDollarSign,
  },
  refunded: {
    label: 'Erstattet',
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400',
    icon: CircleDollarSign,
  },
  partially_refunded: {
    label: 'Teil-erstattet',
    color: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-400',
    icon: CircleDollarSign,
  },
  legacy_paid: {
    label: 'Abgerechnet',
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    icon: CheckCircle,
  },
  expired: {
    label: 'Sitzung abgelaufen',
    color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
    icon: XCircle,
  },
};

const SHIPPING_CONFIG = {
  pending_dispatch: {
    label: 'Versand offen',
    color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400',
    icon: Package,
  },
  shipped: {
    label: 'Versendet',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400',
    icon: Truck,
  },
  delivered: {
    label: 'Geliefert',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    icon: PackageCheck,
  },
  delivered_legacy: {
    label: 'Abgeschlossen',
    color: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
    icon: CheckCircle,
  },
};

const ShipDialog = ({ open, onOpenChange, txn, carriers, onSuccess }) => {
  const [trackingNumber, setTrackingNumber] = useState('');
  const [carrier, setCarrier] = useState('');
  const [shippedAt, setShippedAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [deliveryNoteUrl, setDeliveryNoteUrl] = useState('');
  const [deliveryNoteFilename, setDeliveryNoteFilename] = useState('');
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setTrackingNumber('');
      setCarrier('');
      setShippedAt(new Date().toISOString().slice(0, 10));
      setNotes('');
      setDeliveryNoteUrl('');
      setDeliveryNoteFilename('');
    }
  }, [open]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await uploadShippingDocument(file, txn?.transaction_id);
      setDeliveryNoteUrl(res.data.file_url);
      setDeliveryNoteFilename(res.data.original_filename);
      toast.success('Lieferschein hochgeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!carrier) {
      toast.error('Bitte Spediteur auswählen');
      return;
    }
    setSubmitting(true);
    try {
      await markTransactionShipped(txn.transaction_id, {
        tracking_number: trackingNumber || null,
        carrier,
        shipped_at: shippedAt ? new Date(shippedAt + 'T12:00:00Z').toISOString() : null,
        delivery_note_url: deliveryNoteUrl || null,
        notes: notes || null,
      });
      toast.success('Versand markiert — Käufer wurde benachrichtigt');
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="ship-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-500" />
            Lieferung als versendet markieren
          </DialogTitle>
          <DialogDescription>
            Bitte geben Sie Versanddetails an. Der Käufer wird automatisch per E-Mail benachrichtigt.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-muted/50 rounded-lg p-3 text-sm">
            <p className="font-medium">{txn?.material?.common_title || 'Artikel'}</p>
            <p className="text-muted-foreground text-xs">Menge: {txn?.qty} · An: {txn?.buyer_group?.group_name || '—'}</p>
          </div>
          <div>
            <Label className="text-sm">Spediteur *</Label>
            <Select value={carrier} onValueChange={setCarrier}>
              <SelectTrigger className="mt-1" data-testid="ship-carrier-select">
                <SelectValue placeholder="Spediteur auswählen" />
              </SelectTrigger>
              <SelectContent>
                {carriers.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Sendungsnummer (optional)</Label>
            <Input
              value={trackingNumber}
              onChange={(e) => setTrackingNumber(e.target.value)}
              placeholder="z.B. 1234567890"
              className="mt-1"
              data-testid="ship-tracking-input"
            />
          </div>
          <div>
            <Label className="text-sm">Versanddatum</Label>
            <Input
              type="date"
              value={shippedAt}
              onChange={(e) => setShippedAt(e.target.value)}
              className="mt-1"
              data-testid="ship-date-input"
            />
          </div>
          <div>
            <Label className="text-sm">Lieferschein (PDF/JPG/PNG, optional)</Label>
            <div className="mt-1 flex items-center gap-2">
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp"
                id="ship-doc-upload"
                className="hidden"
                onChange={handleFile}
                data-testid="ship-doc-input"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('ship-doc-upload').click()}
                disabled={uploading}
                data-testid="ship-doc-upload-btn"
              >
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Upload className="h-4 w-4 mr-1" />}
                {deliveryNoteUrl ? 'Ersetzen' : 'Hochladen'}
              </Button>
              {deliveryNoteFilename && (
                <span className="text-xs text-muted-foreground truncate flex-1">{deliveryNoteFilename}</span>
              )}
            </div>
          </div>
          <div>
            <Label className="text-sm">Hinweise (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="z.B. Lieferzeit, Versandfenster …"
              rows={2}
              className="mt-1"
              data-testid="ship-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            className="bg-blue-500 hover:bg-blue-600"
            onClick={handleSubmit}
            disabled={submitting || !carrier}
            data-testid="ship-confirm-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Truck className="h-4 w-4 mr-1" />}
            Versendet markieren
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const DeliverDialog = ({ open, onOpenChange, txn, onSuccess }) => {
  const [deliveredAt, setDeliveredAt] = useState(new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setDeliveredAt(new Date().toISOString().slice(0, 10));
      setNotes('');
    }
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      await markTransactionDelivered(txn.transaction_id, {
        delivered_at: deliveredAt ? new Date(deliveredAt + 'T12:00:00Z').toISOString() : null,
        notes: notes || null,
      });
      toast.success('Empfang bestätigt — Verkäufer wurde benachrichtigt');
      onSuccess();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Aktion fehlgeschlagen');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="deliver-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackageCheck className="h-5 w-5 text-green-500" />
            Empfang der Lieferung bestätigen
          </DialogTitle>
          <DialogDescription>
            Sobald Sie bestätigen, wird die Transaktion final abgeschlossen und der Verkäufer benachrichtigt.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="bg-muted/50 rounded-lg p-3 text-sm space-y-1">
            <p className="font-medium">{txn?.material?.common_title || 'Artikel'}</p>
            {txn?.carrier && (
              <p className="text-muted-foreground text-xs">Spediteur: <span className="font-medium text-foreground">{txn.carrier}</span></p>
            )}
            {txn?.tracking_number && (
              <p className="text-muted-foreground text-xs">Sendungsnummer: <span className="font-medium text-foreground">{txn.tracking_number}</span></p>
            )}
            {txn?.shipped_at && (
              <p className="text-muted-foreground text-xs">Versendet am: {new Date(txn.shipped_at).toLocaleDateString('de-DE')}</p>
            )}
            {txn?.delivery_note_url && (
              <a
                href={txn.delivery_note_url}
                target="_blank"
                rel="noreferrer"
                className="text-xs text-blue-600 hover:underline flex items-center gap-1 mt-1"
                data-testid="deliver-doc-link"
              >
                <ExternalLink className="h-3 w-3" /> Lieferschein öffnen
              </a>
            )}
          </div>
          <div>
            <Label className="text-sm">Liefertermin</Label>
            <Input
              type="date"
              value={deliveredAt}
              onChange={(e) => setDeliveredAt(e.target.value)}
              className="mt-1"
              data-testid="deliver-date-input"
            />
          </div>
          <div>
            <Label className="text-sm">Bemerkungen (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="z.B. Zustand der Ware, Anmerkungen …"
              rows={2}
              className="mt-1"
              data-testid="deliver-notes-input"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Abbrechen</Button>
          <Button
            className="bg-green-500 hover:bg-green-600"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="deliver-confirm-btn"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <PackageCheck className="h-4 w-4 mr-1" />}
            Empfang bestätigen
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StarRating = ({ value, onChange, readonly = false }) => {
  const [hover, setHover] = useState(0);
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          disabled={readonly}
          onClick={() => !readonly && onChange?.(star)}
          onMouseEnter={() => !readonly && setHover(star)}
          onMouseLeave={() => setHover(0)}
          className={`transition-colors ${readonly ? 'cursor-default' : 'cursor-pointer'}`}
          data-testid={`star-${star}`}
        >
          <Star
            className={`h-5 w-5 ${
              star <= (hover || value)
                ? 'fill-amber-400 text-amber-400'
                : 'text-gray-300 dark:text-gray-600'
            }`}
          />
        </button>
      ))}
    </div>
  );
};

const GroupRatingDisplay = ({ groupId, groupName }) => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      const res = await getGroupRatings(groupId);
      setData(res.data);
      setOpen(true);
    } catch { /* ignore */ }
  };

  return (
    <>
      <button onClick={load} className="text-xs text-orange-600 hover:underline flex items-center gap-1" data-testid={`view-rating-${groupId}`}>
        <Star className="h-3 w-3" /> {t('transactions.reviews')}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md" data-testid="group-rating-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              {groupName || data?.group_name || t('transactions.group')} - {t('transactions.reviews')}
            </DialogTitle>
          </DialogHeader>
          {data && data.total_ratings > 0 ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
                <div className="text-3xl font-bold text-amber-600">{data.average_overall}</div>
                <div>
                  <StarRating value={Math.round(data.average_overall)} readonly />
                  <p className="text-xs text-muted-foreground mt-1">{data.total_ratings} {t('transactions.review')}</p>
                </div>
              </div>
              <div className="space-y-2">
                {data.categories.map((cat) => (
                  <div key={cat.category_id} className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{cat.name_de}</span>
                    <div className="flex items-center gap-2">
                      <StarRating value={Math.round(cat.average)} readonly />
                      <span className="font-medium w-8 text-right">{cat.average}</span>
                    </div>
                  </div>
                ))}
              </div>
              {data.recent_comments.length > 0 && (
                <div>
                  <p className="text-sm font-medium mb-2 flex items-center gap-1"><MessageSquare className="h-4 w-4" /> {t('transactions.comments')}</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {data.recent_comments.map((c, i) => (
                      <p key={i} className="text-sm text-muted-foreground bg-muted/50 p-2 rounded">"{c.comment}"</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <p className="text-muted-foreground text-sm py-4 text-center">{t('transactions.noReviews')}</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export const TransactionsPage = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [ratingDialog, setRatingDialog] = useState({ open: false, txn: null });
  const [shipDialog, setShipDialog] = useState({ open: false, txn: null });
  const [deliverDialog, setDeliverDialog] = useState({ open: false, txn: null });
  const [carriers, setCarriers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [scores, setScores] = useState({});
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [payingTxnId, setPayingTxnId] = useState(null);
  const [timelineDialog, setTimelineDialog] = useState({ open: false, transactionId: null });
  const [cancelDialog, setCancelDialog] = useState({ open: false, txn: null });
  const [disputeDialog, setDisputeDialog] = useState({ open: false, txn: null });
  const [disputeThreadDialog, setDisputeThreadDialog] = useState({ open: false, transactionId: null });

  const handlePay = async (txn) => {
    if (!txn?.transaction_id) return;
    setPayingTxnId(txn.transaction_id);
    try {
      const res = await createMarketplacePaymentCheckout(txn.transaction_id, window.location.origin);
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        toast.error('Stripe-Sitzung konnte nicht erstellt werden');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Zahlung konnte nicht gestartet werden');
      setPayingTxnId(null);
    }
  };

  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState(null);

  const handleDownloadInvoice = async (txn) => {
    if (!txn?.transaction_id) return;
    setDownloadingInvoiceId(txn.transaction_id);
    try {
      const res = await downloadMarketplaceInvoice(txn.transaction_id);
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Rechnung_${txn.invoice_number || txn.order_number || txn.transaction_id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Rechnung heruntergeladen');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rechnung konnte nicht geladen werden');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  const STATUS_CONFIG = {
    completed: { label: t('transactions.statusCompleted'), color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400', icon: CheckCircle },
    cancelled: { label: t('transactions.statusCancelled'), color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400', icon: XCircle },
    pending: { label: t('transactions.statusPending'), color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400', icon: Clock },
  };

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      const [txnRes, catRes] = await Promise.all([
        getTransactions(params),
        getRatingCategories()
      ]);
      setTransactions(txnRes.data);
      setCategories(catRes.data);
    } catch (err) {
      if (err.response?.status !== 401) toast.error(t('transactions.loadError'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, t]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    getShippingCarriers()
      .then((r) => setCarriers(r.data.carriers || []))
      .catch(() => setCarriers([]));
  }, []);

  const openRating = (txn) => {
    setScores({});
    setComment('');
    setRatingDialog({ open: true, txn });
  };

  const handleSubmitRating = async () => {
    const txn = ratingDialog.txn;
    if (!txn) return;
    const scoreEntries = Object.entries(scores).filter(([, v]) => v > 0);
    if (scoreEntries.length === 0) {
      toast.error(t('transactions.ratingRequired'));
      return;
    }
    setSubmitting(true);
    try {
      await submitRating({
        transaction_id: txn.transaction_id,
        scores: scoreEntries.map(([category_id, score]) => ({ category_id, score })),
        comment: comment.trim() || null,
      });
      toast.success(t('transactions.ratingSuccess'));
      setRatingDialog({ open: false, txn: null });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('transactions.ratingError'));
    } finally {
      setSubmitting(false);
    }
  };

  const isSeller = (txn) => txn.seller_group_id === user?.group_id;
  const counterpartyGroup = (txn) => isSeller(txn) ? txn.buyer_group : txn.seller_group;

  const stats = {
    total: transactions.length,
    completed: transactions.filter(tx => tx.status === 'completed').length,
    totalValue: transactions.reduce((s, tx) => s + (tx.price_EUR || 0), 0),
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold" data-testid="transactions-title">{t('transactions.title')}</h1>
            <p className="text-sm text-muted-foreground mt-1">{t('transactions.subtitle')}</p>
          </div>
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-muted-foreground" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[160px]" data-testid="status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('transactions.allStatuses')}</SelectItem>
                <SelectItem value="completed">{t('transactions.statusCompleted')}</SelectItem>
                <SelectItem value="cancelled">{t('transactions.statusCancelled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                  <ArrowLeftRight className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="total-transactions">{stats.total}</p>
                  <p className="text-xs text-muted-foreground">{t('transactions.total')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="completed-count">{stats.completed}</p>
                  <p className="text-xs text-muted-foreground">{t('transactions.completed')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-orange-100 dark:bg-orange-900/30 rounded-lg">
                  <TrendingUp className="h-5 w-5 text-orange-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="total-value">{fmt2(stats.totalValue)} EUR</p>
                  <p className="text-xs text-muted-foreground">{t('transactions.totalValue')}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
          </div>
        ) : transactions.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <ArrowLeftRight className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground">{t('transactions.empty')}</p>
              <p className="text-xs text-muted-foreground mt-1">{t('transactions.emptyDesc')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="border rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="transactions-table">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="text-left px-4 py-3 font-medium">Bestell-Nr.</th>
                    <th className="text-left px-4 py-3 font-medium">{t('transactions.date')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('transactions.material')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('transactions.type')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('transactions.seller')}</th>
                    <th className="text-left px-4 py-3 font-medium">{t('transactions.buyer')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('transactions.quantity')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('transactions.value')}</th>
                    <th className="text-right px-4 py-3 font-medium">{t('transactions.fee')}</th>
                    <th className="text-center px-4 py-3 font-medium">{t('transactions.status')}</th>
                    <th className="text-center px-4 py-3 font-medium">Zahlung</th>
                    <th className="text-center px-4 py-3 font-medium">Versand</th>
                    <th className="text-center px-4 py-3 font-medium">{t('transactions.rating')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {transactions.map((txn) => {
                    const cfg = STATUS_CONFIG[txn.status] || STATUS_CONFIG.pending;
                    const StatusIcon = cfg.icon;
                    const cp = counterpartyGroup(txn);
                    const canRate = txn.status === 'completed' && !txn.user_has_rated && cp;
                    const isUserSeller = isSeller(txn);
                    const isUserBuyer = !isUserSeller && (txn.buyer_group_id === user?.group_id || txn.buyer_user_id === user?.user_id);
                    const paymentStatus = txn.payment_status || (txn.status === 'completed' ? 'legacy_paid' : 'unpaid');
                    const payCfg = PAYMENT_CONFIG[paymentStatus] || PAYMENT_CONFIG.unpaid;
                    const PayIcon = payCfg.icon;
                    const paymentSettled = paymentStatus === 'paid' || paymentStatus === 'legacy_paid';
                    const canPay = txn.status === 'completed' && !paymentSettled && (isUserBuyer || user?.role === 'admin');
                    const shipStatus = txn.shipping_status || (txn.status === 'completed' ? 'pending_dispatch' : null);
                    const shipCfg = shipStatus ? SHIPPING_CONFIG[shipStatus] : null;
                    const canMarkShipped = txn.status === 'completed' && shipStatus === 'pending_dispatch' && paymentSettled && (isUserSeller || user?.role === 'admin');
                    const canMarkDelivered = txn.status === 'completed' && shipStatus === 'shipped' && (!isUserSeller || user?.role === 'admin');
                    // Phase D: Cancel (pre-shipping) — buyer, seller, or admin
                    const canCancel = (
                      txn.status === 'completed' &&
                      !['shipped', 'delivered', 'delivered_legacy'].includes(shipStatus) &&
                      paymentStatus !== 'refunded' &&
                      paymentStatus !== 'partially_refunded' &&
                      (isUserBuyer || isUserSeller || user?.role === 'admin')
                    );
                    // Phase D: Dispute (post-shipping) — buyer or admin, no existing open dispute
                    const canDispute = (
                      txn.status === 'completed' &&
                      ['shipped', 'delivered'].includes(shipStatus) &&
                      !txn.has_open_dispute &&
                      !txn.dispute_id &&
                      (isUserBuyer || user?.role === 'admin')
                    );
                    const hasDispute = !!txn.dispute_id;
                    return (
                      <tr key={txn.transaction_id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`txn-row-${txn.transaction_id}`}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setTimelineDialog({ open: true, transactionId: txn.transaction_id })}
                            className="inline-flex items-center gap-1 font-mono text-xs font-semibold text-orange-600 hover:text-orange-700 dark:text-orange-400 dark:hover:text-orange-300 hover:underline"
                            data-testid={`order-number-${txn.transaction_id}`}
                            title="Bestelldetails & Verlauf anzeigen"
                          >
                            <Hash className="h-3 w-3" />
                            {txn.order_number || txn.transaction_id.slice(0, 8).toUpperCase()}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">
                          {txn.completed_at ? new Date(txn.completed_at).toLocaleDateString('de-DE') : new Date(txn.created_at).toLocaleDateString('de-DE')}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-muted-foreground shrink-0" />
                            <span className="truncate max-w-[200px]">{txn.material?.common_title || txn.material?.manufacturer_part_number || '-'}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-xs whitespace-nowrap">
                            {txn.transaction_type === 'internal_transfer' ? t('transactions.internal') : t('transactions.external')}
                          </Badge>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[120px]">{txn.seller_group?.group_name || '-'}</span>
                            {txn.seller_group && <GroupRatingDisplay groupId={txn.seller_group.group_id} groupName={txn.seller_group.group_name} />}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <span className="truncate max-w-[120px]">{txn.buyer_group?.group_name || '-'}</span>
                            {txn.buyer_group && <GroupRatingDisplay groupId={txn.buyer_group.group_id} groupName={txn.buyer_group.group_name} />}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-right font-medium">{txn.qty || '-'}</td>
                        <td className="px-4 py-3 text-right font-medium">{txn.price_EUR ? `${fmt2(txn.price_EUR)} EUR` : '-'}</td>
                        <td className="px-4 py-3 text-right text-muted-foreground">{txn.fee_EUR ? `${fmt2(txn.fee_EUR)} EUR` : '-'}</td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex flex-col items-center gap-1.5">
                            <Badge className={cfg.color}>
                              <StatusIcon className="h-3 w-3 mr-1" />
                              {cfg.label}
                            </Badge>
                            {canCancel && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-red-300 text-red-700 hover:bg-red-50 dark:text-red-400 dark:border-red-700 dark:hover:bg-red-900/20"
                                onClick={() => setCancelDialog({ open: true, txn })}
                                data-testid={`cancel-btn-${txn.transaction_id}`}
                                title={paymentSettled ? 'Stornieren & Erstatten' : 'Stornieren'}
                              >
                                <Ban className="h-3 w-3 mr-1" />
                                Stornieren
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center" data-testid={`payment-cell-${txn.transaction_id}`}>
                          <div className="flex flex-col items-center gap-1.5">
                            <Badge className={payCfg.color}>
                              <PayIcon className="h-3 w-3 mr-1" />
                              {payCfg.label}
                            </Badge>
                            {txn.payment_total_EUR != null && !paymentSettled && (
                              <span className="text-[10px] text-muted-foreground font-medium">
                                {fmt2(txn.payment_total_EUR)} EUR
                              </span>
                            )}
                            {canPay && (
                              <Button
                                size="sm"
                                className="h-7 px-2 bg-orange-500 hover:bg-orange-600 text-xs"
                                onClick={() => handlePay(txn)}
                                disabled={payingTxnId === txn.transaction_id}
                                data-testid={`pay-btn-${txn.transaction_id}`}
                              >
                                {payingTxnId === txn.transaction_id ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <CreditCard className="h-3 w-3 mr-1" />
                                )}
                                Jetzt bezahlen
                              </Button>
                            )}
                            {paymentSettled && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-7 px-2 text-xs border-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
                                onClick={() => handleDownloadInvoice(txn)}
                                disabled={downloadingInvoiceId === txn.transaction_id}
                                data-testid={`download-invoice-btn-${txn.transaction_id}`}
                                title={txn.invoice_number ? `Rechnung ${txn.invoice_number}` : 'Rechnung als PDF'}
                              >
                                {downloadingInvoiceId === txn.transaction_id ? (
                                  <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                                ) : (
                                  <FileText className="h-3 w-3 mr-1" />
                                )}
                                Rechnung
                              </Button>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center" data-testid={`ship-cell-${txn.transaction_id}`}>
                          {shipCfg ? (
                            <div className="flex flex-col items-center gap-1.5">
                              <Badge className={shipCfg.color}>
                                {(() => { const ShipIcon = shipCfg.icon; return <ShipIcon className="h-3 w-3 mr-1" />; })()}
                                {shipCfg.label}
                              </Badge>
                              {txn.tracking_number && (
                                <span className="text-[10px] text-muted-foreground font-mono">{txn.tracking_number}</span>
                              )}
                              {canMarkShipped && (
                                <Button
                                  size="sm"
                                  className="h-7 px-2 bg-blue-500 hover:bg-blue-600 text-xs"
                                  onClick={() => setShipDialog({ open: true, txn })}
                                  data-testid={`mark-shipped-btn-${txn.transaction_id}`}
                                >
                                  <Truck className="h-3 w-3 mr-1" /> Versendet
                                </Button>
                              )}
                              {!paymentSettled && shipStatus === 'pending_dispatch' && isUserSeller && (
                                <span className="text-[10px] text-amber-600 dark:text-amber-400 italic" data-testid={`waiting-payment-hint-${txn.transaction_id}`}>
                                  Versand nach Zahlung
                                </span>
                              )}
                              {canMarkDelivered && (
                                <Button
                                  size="sm"
                                  className="h-7 px-2 bg-green-500 hover:bg-green-600 text-xs"
                                  onClick={() => setDeliverDialog({ open: true, txn })}
                                  data-testid={`mark-delivered-btn-${txn.transaction_id}`}
                                >
                                  <PackageCheck className="h-3 w-3 mr-1" /> Empfangen
                                </Button>
                              )}
                              {canDispute && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-amber-300 text-amber-700 hover:bg-amber-50 dark:text-amber-400 dark:border-amber-700 dark:hover:bg-amber-900/20"
                                  onClick={() => setDisputeDialog({ open: true, txn })}
                                  data-testid={`dispute-btn-${txn.transaction_id}`}
                                  title="Reklamation eröffnen"
                                >
                                  <AlertTriangle className="h-3 w-3 mr-1" /> Reklamieren
                                </Button>
                              )}
                              {hasDispute && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-7 px-2 text-xs border-amber-400 text-amber-800 bg-amber-50 hover:bg-amber-100 dark:bg-amber-900/30 dark:text-amber-300 dark:border-amber-700"
                                  onClick={() => setDisputeThreadDialog({ open: true, transactionId: txn.transaction_id })}
                                  data-testid={`dispute-view-btn-${txn.transaction_id}`}
                                  title="Reklamation anzeigen"
                                >
                                  <MessageSquare className="h-3 w-3 mr-1" /> Reklamation
                                </Button>
                              )}
                            </div>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {canRate ? (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-amber-600 border-amber-300 hover:bg-amber-50"
                              onClick={() => openRating(txn)}
                              data-testid={`rate-btn-${txn.transaction_id}`}
                            >
                              <Star className="h-3.5 w-3.5 mr-1" /> {t('transactions.rate')}
                            </Button>
                          ) : txn.user_has_rated ? (
                            <Badge variant="outline" className="text-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" /> {t('transactions.rated')}
                            </Badge>
                          ) : (
                            <span className="text-muted-foreground text-xs">-</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </main>
      <Footer />

      <ShipDialog
        open={shipDialog.open}
        onOpenChange={(open) => setShipDialog({ open, txn: open ? shipDialog.txn : null })}
        txn={shipDialog.txn}
        carriers={carriers}
        onSuccess={loadData}
      />
      <DeliverDialog
        open={deliverDialog.open}
        onOpenChange={(open) => setDeliverDialog({ open, txn: open ? deliverDialog.txn : null })}
        txn={deliverDialog.txn}
        onSuccess={loadData}
      />
      <OrderTimelineDialog
        open={timelineDialog.open}
        onOpenChange={(open) => setTimelineDialog({ open, transactionId: open ? timelineDialog.transactionId : null })}
        transactionId={timelineDialog.transactionId}
      />
      <CancelDialog
        open={cancelDialog.open}
        onOpenChange={(open) => setCancelDialog({ open, txn: open ? cancelDialog.txn : null })}
        txn={cancelDialog.txn}
        onSuccess={loadData}
      />
      <DisputeDialog
        open={disputeDialog.open}
        onOpenChange={(open) => setDisputeDialog({ open, txn: open ? disputeDialog.txn : null })}
        txn={disputeDialog.txn}
        onSuccess={loadData}
      />
      <DisputeThreadDialog
        open={disputeThreadDialog.open}
        onOpenChange={(open) => setDisputeThreadDialog({ open, transactionId: open ? disputeThreadDialog.transactionId : null })}
        transactionId={disputeThreadDialog.transactionId}
        currentUserRole={user?.role}
      />

      <Dialog open={ratingDialog.open} onOpenChange={(open) => setRatingDialog({ open, txn: open ? ratingDialog.txn : null })}>
        <DialogContent className="max-w-md" data-testid="rating-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="h-5 w-5 text-amber-500" />
              {t('transactions.rateTitle')}
            </DialogTitle>
          </DialogHeader>
          {ratingDialog.txn && (
            <div className="space-y-4">
              <div className="bg-muted/50 rounded-lg p-3 text-sm">
                <p className="font-medium">{ratingDialog.txn.material?.common_title || t('transactions.material')}</p>
                <p className="text-muted-foreground">
                  {t('transactions.counterparty')}: {counterpartyGroup(ratingDialog.txn)?.group_name || t('transactions.unknown')}
                </p>
              </div>
              <div className="space-y-3">
                {categories.map((cat) => (
                  <div key={cat.category_id} className="flex items-center justify-between">
                    <Label className="text-sm">{cat.name_de}</Label>
                    <StarRating
                      value={scores[cat.category_id] || 0}
                      onChange={(v) => setScores(s => ({ ...s, [cat.category_id]: v }))}
                    />
                  </div>
                ))}
              </div>
              <div>
                <Label className="text-sm">{t('transactions.commentLabel')}</Label>
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  placeholder={t('transactions.commentPlaceholder')}
                  className="mt-1"
                  rows={3}
                  data-testid="rating-comment"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRatingDialog({ open: false, txn: null })}>{t('common.cancel')}</Button>
            <Button
              className="bg-amber-500 hover:bg-amber-600"
              onClick={handleSubmitRating}
              disabled={submitting}
              data-testid="submit-rating-btn"
            >
              {submitting ? t('transactions.submitting') : t('transactions.submitRating')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
